import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import CategoryFormModal from '../../components/admin/CategoryFormModal';
import { apiRequest, getImageUrl } from '../../utils/api';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Drag Handle Icon
const DragHandleIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
    </svg>
);

// Arrow Up Icon
const ArrowUpIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
);

// Arrow Down Icon
const ArrowDownIcon = ({ className = "w-4 h-4" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, categoryName, hasSubcategories, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
                
                <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                    <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    
                    <h3 className="text-lg font-bold text-center text-gray-900 mb-2">
                        Kategoriyi Sil
                    </h3>
                    
                    <p className="text-sm text-center text-gray-600 mb-4">
                        <span className="font-semibold text-gray-900">"{categoryName}"</span> kategorisini silmek istediğinize emin misiniz?
                    </p>
                    
                    {hasSubcategories && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                            <div className="flex items-start gap-2">
                                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-yellow-800">
                                    Bu kategorinin alt kategorileri var. Silme işlemi sonrası alt kategoriler ana kategoriye taşınacaktır.
                                </p>
                            </div>
                        </div>
                    )}
                    
                    <p className="text-xs text-center text-gray-500 mb-6">
                        Bu işlem geri alınamaz. Kategoriye ait ürünler kategorisiz kalacaktır.
                    </p>
                    
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
                        >
                            İptal
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Siliniyor...
                                </>
                            ) : (
                                'Evet, Sil'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Toggle Switch Component
const ToggleSwitch = ({ enabled, onChange, disabled }) => {
    return (
        <button
            type="button"
            onClick={onChange}
            disabled={disabled}
            className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${enabled ? 'bg-green-500' : 'bg-gray-300'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
        >
            <span
                className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${enabled ? 'translate-x-5' : 'translate-x-0'}
                `}
            />
        </button>
    );
};

// Sortable Item Component (for both mobile and desktop)
const SortableItem = ({ 
    category, 
    isSubcategory, 
    parentName,
    siblings,
    currentIndex,
    onEdit, 
    onDelete, 
    onToggleStatus, 
    onMoveUp,
    onMoveDown,
    onSortOrderChange,
    togglingStatus,
    savingOrder,
    isMobile,
    subcatsComponent
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: category.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    const [editingSortOrder, setEditingSortOrder] = useState(false);
    const [sortOrderValue, setSortOrderValue] = useState(category.sort_order || 0);

    const handleSortOrderSubmit = () => {
        const newOrder = parseInt(sortOrderValue, 10);
        if (!isNaN(newOrder) && newOrder !== category.sort_order) {
            onSortOrderChange(category, newOrder);
        }
        setEditingSortOrder(false);
    };

    if (isMobile) {
        return (
            <div ref={setNodeRef} style={style}>
                <div className={`${isSubcategory ? 'ml-4 border-l-4 border-blue-200 pl-3' : ''}`}>
                    <div className={`bg-white rounded-xl shadow-sm border ${category.status === 'active' ? 'border-gray-100' : 'border-gray-200 bg-gray-50'} p-4 mb-3 ${isDragging ? 'shadow-xl ring-2 ring-blue-400' : ''}`}>
                        <div className="flex items-start gap-3">
                            {/* Drag Handle & Sort Controls */}
                            <div className="flex flex-col items-center gap-1">
                                <button
                                    {...attributes}
                                    {...listeners}
                                    className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none"
                                >
                                    <DragHandleIcon className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => onMoveUp(category, siblings)}
                                    disabled={currentIndex === 0 || savingOrder}
                                    className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ArrowUpIcon className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => onMoveDown(category, siblings)}
                                    disabled={currentIndex === siblings.length - 1 || savingOrder}
                                    className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ArrowDownIcon className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Image */}
                            {category.image ? (
                                <img
                                    src={getImageUrl(category.image)}
                                    alt={category.name}
                                    className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                                />
                            ) : (
                                <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-xl">{isSubcategory ? '📂' : '📁'}</span>
                                </div>
                            )}
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    {isSubcategory && (
                                        <span className="text-[10px] text-blue-500 font-semibold uppercase bg-blue-50 px-1.5 py-0.5 rounded">Alt</span>
                                    )}
                                    <h3 className="font-semibold text-gray-900 truncate">{category.name}</h3>
                                </div>
                                {category.description && (
                                    <p className="text-sm text-gray-500 line-clamp-1 mb-2">{category.description}</p>
                                )}
                                
                                {/* Status Toggle */}
                                <div className="flex items-center gap-2">
                                    <ToggleSwitch
                                        enabled={category.status === 'active'}
                                        onChange={() => onToggleStatus(category)}
                                        disabled={togglingStatus === category.id}
                                    />
                                    <span className={`text-xs font-medium ${category.status === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                                        {category.status === 'active' ? 'Aktif' : 'Pasif'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                            {/* Editable Sort Order */}
                            {editingSortOrder ? (
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        value={sortOrderValue}
                                        onChange={(e) => setSortOrderValue(e.target.value)}
                                        onBlur={handleSortOrderSubmit}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSortOrderSubmit()}
                                        className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleSortOrderSubmit}
                                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                                    >
                                        ✓
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => {
                                        setSortOrderValue(category.sort_order || 0);
                                        setEditingSortOrder(true);
                                    }}
                                    className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded hover:bg-gray-200 transition"
                                >
                                    Sıra: {category.sort_order || 0}
                                </button>
                            )}
                            
                            {/* Edit & Delete */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => onEdit(category)}
                                    className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                                >
                                    Düzenle
                                </button>
                                <button
                                    onClick={() => onDelete(category)}
                                    className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
                                >
                                    Sil
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Render subcategories */}
                {subcatsComponent}
            </div>
        );
    }

    // Desktop Row
    const hasChildren = subcatsComponent !== null;

    return (
        <>
            <tr 
                ref={setNodeRef}
                style={style}
                className={`
                    ${isSubcategory 
                        ? 'bg-gradient-to-r from-blue-50/50 to-transparent border-l-4 border-l-blue-400' 
                        : 'bg-white border-l-4 border-l-transparent hover:border-l-gray-300'
                    } 
                    ${category.status !== 'active' ? 'opacity-50' : ''} 
                    ${isDragging ? 'opacity-50 bg-blue-100' : 'hover:bg-gray-50/80'}
                    transition-all duration-200
                `}
            >
                {/* Drag Handle & Sort Controls */}
                <td className="px-2 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                        <button
                            {...attributes}
                            {...listeners}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-grab active:cursor-grabbing touch-none transition-colors"
                        >
                            <DragHandleIcon className="w-5 h-5" />
                        </button>
                        <div className="flex flex-col">
                            <button
                                onClick={() => onMoveUp(category, siblings)}
                                disabled={currentIndex === 0 || savingOrder}
                                className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <ArrowUpIcon className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => onMoveDown(category, siblings)}
                                disabled={currentIndex === siblings.length - 1 || savingOrder}
                                className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <ArrowDownIcon className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </td>

                {/* Sort Order (Editable) */}
                <td className="px-2 py-4 whitespace-nowrap">
                    {editingSortOrder ? (
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                value={sortOrderValue}
                                onChange={(e) => setSortOrderValue(e.target.value)}
                                onBlur={handleSortOrderSubmit}
                                onKeyDown={(e) => e.key === 'Enter' && handleSortOrderSubmit()}
                                className="w-14 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                autoFocus
                            />
                        </div>
                    ) : (
                        <button
                            onClick={() => {
                                setSortOrderValue(category.sort_order || 0);
                                setEditingSortOrder(true);
                            }}
                            className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md hover:bg-gray-200 transition cursor-pointer"
                            title="Sıra numarasını düzenlemek için tıklayın"
                        >
                            {category.sort_order || 0}
                        </button>
                    )}
                </td>
                
                {/* Image */}
                <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`relative ${isSubcategory ? 'ml-4' : ''}`}>
                        {category.image ? (
                            <img
                                src={getImageUrl(category.image)}
                                alt={category.name}
                                className={`object-cover rounded-xl shadow-sm ${isSubcategory ? 'w-10 h-10' : 'w-14 h-14 ring-2 ring-gray-100'}`}
                            />
                        ) : (
                            <div className={`bg-gradient-to-br ${isSubcategory ? 'from-blue-100 to-blue-200 w-10 h-10' : 'from-gray-100 to-gray-200 w-14 h-14'} rounded-xl flex items-center justify-center shadow-sm`}>
                                <span className={`${isSubcategory ? 'text-base' : 'text-xl'}`}>
                                    {isSubcategory ? '📂' : '📁'}
                                </span>
                            </div>
                        )}
                    </div>
                </td>
                
                {/* Name & Hierarchy */}
                <td className="px-4 py-4">
                    <div className={`${isSubcategory ? 'ml-4' : ''}`}>
                        {/* Breadcrumb for subcategory */}
                        {isSubcategory && parentName && (
                            <div className="flex items-center gap-1 mb-1">
                                <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="text-[10px] text-blue-500 font-medium uppercase tracking-wide">
                                    Alt Kategori
                                </span>
                            </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                            <div className={`font-semibold ${isSubcategory ? 'text-sm text-gray-700' : 'text-base text-gray-900'}`}>
                                {category.name}
                            </div>
                            
                            {/* Main category indicator */}
                            {!isSubcategory && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 uppercase tracking-wide">
                                    Ana
                                </span>
                            )}
                        </div>
                        
                        {category.description && (
                            <div className={`text-gray-500 truncate max-w-xs mt-0.5 ${isSubcategory ? 'text-xs' : 'text-sm'}`}>
                                {category.description}
                            </div>
                        )}
                    </div>
                </td>
                
                {/* Status Toggle */}
                <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                        <ToggleSwitch
                            enabled={category.status === 'active'}
                            onChange={() => onToggleStatus(category)}
                            disabled={togglingStatus === category.id}
                        />
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                            category.status === 'active' 
                                ? 'text-green-700 bg-green-100' 
                                : 'text-gray-600 bg-gray-100'
                        }`}>
                            {category.status === 'active' ? 'Aktif' : 'Pasif'}
                        </span>
                    </div>
                </td>
                
                {/* Actions */}
                <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-end gap-2">
                        <button
                            onClick={() => onEdit(category)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Düzenle
                        </button>
                        <button
                            onClick={() => onDelete(category)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Sil
                        </button>
                    </div>
                </td>
            </tr>

            {/* Render subcategories */}
            {subcatsComponent}
        </>
    );
};

// Sortable Category List with its own SortableContext
const SortableCategoryList = ({
    categories,
    parentId,
    parentName,
    isSubcategory,
    getSubcategories,
    onEdit,
    onDelete,
    onToggleStatus,
    onMoveUp,
    onMoveDown,
    onSortOrderChange,
    onDragEnd,
    togglingStatus,
    savingOrder,
    isMobile,
    sensors
}) => {
    const categoryIds = useMemo(() => categories.map(c => c.id), [categories]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = categories.findIndex(c => c.id === active.id);
        const newIndex = categories.findIndex(c => c.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            onDragEnd(categories, oldIndex, newIndex, parentId);
        }
    };

    if (categories.length === 0) return null;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
                {categories.map((category, idx) => {
                    const subcats = getSubcategories(category.id);
                    
                    const subcatsComponent = subcats.length > 0 ? (
                        <SortableCategoryList
                            categories={subcats}
                            parentId={category.id}
                            parentName={category.name}
                            isSubcategory={true}
                            getSubcategories={getSubcategories}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onToggleStatus={onToggleStatus}
                            onMoveUp={onMoveUp}
                            onMoveDown={onMoveDown}
                            onSortOrderChange={onSortOrderChange}
                            onDragEnd={onDragEnd}
                            togglingStatus={togglingStatus}
                            savingOrder={savingOrder}
                            isMobile={isMobile}
                            sensors={sensors}
                        />
                    ) : null;

                    return (
                        <SortableItem
                            key={category.id}
                            category={category}
                            isSubcategory={isSubcategory}
                            parentName={parentName}
                            siblings={categories}
                            currentIndex={idx}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onToggleStatus={onToggleStatus}
                            onMoveUp={onMoveUp}
                            onMoveDown={onMoveDown}
                            onSortOrderChange={onSortOrderChange}
                            togglingStatus={togglingStatus}
                            savingOrder={savingOrder}
                            isMobile={isMobile}
                            subcatsComponent={subcatsComponent}
                        />
                    );
                })}
            </SortableContext>
        </DndContext>
    );
};

const CategoriesPage = () => {
    const { organizationId } = useParams();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [savingOrder, setSavingOrder] = useState(false);
    const [togglingStatus, setTogglingStatus] = useState(null);
    
    // Delete modal state
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, category: null });
    const [deleting, setDeleting] = useState(false);

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        loadCategories();
    }, [organizationId]);

    const loadCategories = async () => {
        setLoading(true);
        try {
            const response = await apiRequest(`/organizations/${organizationId}/categories`);
            const sorted = (response.data || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            setCategories(sorted);
        } catch (error) {
            console.error('Load categories error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get main categories (no parent)
    const mainCategories = useMemo(() => 
        categories.filter(c => !c.parent_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)), 
        [categories]
    );
    
    // Get subcategories for a parent
    const getSubcategories = (parentId) => {
        return categories.filter(c => c.parent_id === parentId).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    };

    // Check if category has subcategories
    const hasSubcategories = (categoryId) => {
        return categories.some(c => c.parent_id === categoryId);
    };

    // Toggle status
    const handleToggleStatus = async (category) => {
        setTogglingStatus(category.id);
        try {
            const response = await apiRequest(`/organizations/${organizationId}/categories/${category.id}/toggle-status`, {
                method: 'PATCH'
            });
            
            setCategories(prev => prev.map(c => 
                c.id === category.id ? { ...c, status: response.data.status } : c
            ));
        } catch (error) {
            console.error('Toggle status error:', error);
            alert('Durum güncellenirken bir hata oluştu');
        } finally {
            setTogglingStatus(null);
        }
    };

    // Delete handlers
    const openDeleteModal = (category) => {
        setDeleteModal({ isOpen: true, category });
    };

    const closeDeleteModal = () => {
        setDeleteModal({ isOpen: false, category: null });
    };

    const handleConfirmDelete = async () => {
        if (!deleteModal.category) return;
        
        setDeleting(true);
        try {
            await apiRequest(`/organizations/${organizationId}/categories/${deleteModal.category.id}`, {
                method: 'DELETE'
            });
            closeDeleteModal();
            loadCategories();
        } catch (error) {
            console.error('Delete error:', error);
            alert('Kategori silinirken bir hata oluştu');
        } finally {
            setDeleting(false);
        }
    };

    // Edit handler
    const handleEdit = (category) => {
        setEditingCategory(category);
        setShowModal(true);
    };

    // Update sort order in DB
    const updateSortOrder = async (categoryId, newOrder) => {
        try {
            await apiRequest(`/organizations/${organizationId}/categories/${categoryId}`, {
                method: 'PUT',
                body: JSON.stringify({ sort_order: newOrder }),
                headers: { 'Content-Type': 'application/json' }
            });
        } catch (error) {
            console.error('Update sort order error:', error);
            throw error;
        }
    };

    // Move category up
    const handleMoveUp = async (category, siblings) => {
        const currentIndex = siblings.findIndex(c => c.id === category.id);
        if (currentIndex <= 0 || savingOrder) return;

        const prevCategory = siblings[currentIndex - 1];
        const newCatOrder = prevCategory.sort_order || currentIndex;
        const newPrevOrder = category.sort_order || currentIndex + 1;
        
        const newCategories = categories.map(c => {
            if (c.id === category.id) return { ...c, sort_order: newCatOrder };
            if (c.id === prevCategory.id) return { ...c, sort_order: newPrevOrder };
            return c;
        }).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        
        setCategories(newCategories);
        
        setSavingOrder(true);
        try {
            await Promise.all([
                updateSortOrder(category.id, newCatOrder),
                updateSortOrder(prevCategory.id, newPrevOrder)
            ]);
        } finally {
            setSavingOrder(false);
        }
    };

    // Move category down
    const handleMoveDown = async (category, siblings) => {
        const currentIndex = siblings.findIndex(c => c.id === category.id);
        if (currentIndex >= siblings.length - 1 || savingOrder) return;

        const nextCategory = siblings[currentIndex + 1];
        const newCatOrder = nextCategory.sort_order || currentIndex + 2;
        const newNextOrder = category.sort_order || currentIndex + 1;
        
        const newCategories = categories.map(c => {
            if (c.id === category.id) return { ...c, sort_order: newCatOrder };
            if (c.id === nextCategory.id) return { ...c, sort_order: newNextOrder };
            return c;
        }).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        
        setCategories(newCategories);
        
        setSavingOrder(true);
        try {
            await Promise.all([
                updateSortOrder(category.id, newCatOrder),
                updateSortOrder(nextCategory.id, newNextOrder)
            ]);
        } finally {
            setSavingOrder(false);
        }
    };

    // Handle direct sort order change
    const handleSortOrderChange = async (category, newOrder) => {
        if (savingOrder) return;

        setCategories(prev => {
            const updated = prev.map(c => 
                c.id === category.id ? { ...c, sort_order: newOrder } : c
            );
            return updated.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        });

        setSavingOrder(true);
        try {
            await updateSortOrder(category.id, newOrder);
        } catch (error) {
            loadCategories();
        } finally {
            setSavingOrder(false);
        }
    };

    // Handle drag end from nested SortableContext
    const handleDragEnd = async (siblings, oldIndex, newIndex, parentId) => {
        const reorderedSiblings = arrayMove(siblings, oldIndex, newIndex);
        
        const updates = reorderedSiblings.map((cat, index) => ({
            ...cat,
            sort_order: index + 1
        }));

        setCategories(prev => {
            const newCategories = [...prev];
            updates.forEach(update => {
                const idx = newCategories.findIndex(c => c.id === update.id);
                if (idx !== -1) {
                    newCategories[idx] = update;
                }
            });
            return newCategories.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        });

        setSavingOrder(true);
        try {
            await Promise.all(updates.map(cat => updateSortOrder(cat.id, cat.sort_order)));
        } catch (error) {
            console.error('Failed to save order:', error);
            loadCategories();
        } finally {
            setSavingOrder(false);
        }
    };

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Kategoriler</h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Sürükle-bırak, yukarı/aşağı butonları veya sıra numarası ile sıralayın
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingCategory(null);
                            setShowModal(true);
                        }}
                        className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2 font-medium shadow-lg shadow-blue-600/25"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Yeni Kategori
                    </button>
                </div>

                {savingOrder && (
                    <div className="mb-4 px-4 py-3 bg-blue-50 text-blue-700 rounded-xl flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm">Sıralama kaydediliyor...</span>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : categories.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        </div>
                        <p className="text-gray-500 mb-4">Henüz kategori eklenmemiş</p>
                        <button
                            onClick={() => setShowModal(true)}
                            className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium"
                        >
                            İlk Kategoriyi Ekle
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Mobile View */}
                        <div className="md:hidden space-y-1">
                            <SortableCategoryList
                                categories={mainCategories}
                                parentId={null}
                                parentName={null}
                                isSubcategory={false}
                                getSubcategories={getSubcategories}
                                onEdit={handleEdit}
                                onDelete={openDeleteModal}
                                onToggleStatus={handleToggleStatus}
                                onMoveUp={handleMoveUp}
                                onMoveDown={handleMoveDown}
                                onSortOrderChange={handleSortOrderChange}
                                onDragEnd={handleDragEnd}
                                togglingStatus={togglingStatus}
                                savingOrder={savingOrder}
                                isMobile={true}
                                sensors={sensors}
                            />
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                            <div className="flex items-center gap-1">
                                                <DragHandleIcon className="w-4 h-4 text-gray-400" />
                                                <span>Taşı</span>
                                            </div>
                                        </th>
                                        <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                            Sıra
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                            Görsel
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Kategori Adı
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                            Durum
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                                            İşlemler
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    <SortableCategoryList
                                        categories={mainCategories}
                                        parentId={null}
                                        parentName={null}
                                        isSubcategory={false}
                                        getSubcategories={getSubcategories}
                                        onEdit={handleEdit}
                                        onDelete={openDeleteModal}
                                        onToggleStatus={handleToggleStatus}
                                        onMoveUp={handleMoveUp}
                                        onMoveDown={handleMoveDown}
                                        onSortOrderChange={handleSortOrderChange}
                                        onDragEnd={handleDragEnd}
                                        togglingStatus={togglingStatus}
                                        savingOrder={savingOrder}
                                        isMobile={false}
                                        sensors={sensors}
                                    />
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {/* Category Form Modal */}
                <CategoryFormModal
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setEditingCategory(null);
                    }}
                    category={editingCategory}
                    organizationId={organizationId}
                    categories={categories}
                    onSuccess={async (updatedCategory) => {
                        // Refresh categories list
                        await loadCategories();
                        // If category was updated, update editingCategory with latest data
                        if (updatedCategory && editingCategory) {
                            setEditingCategory(updatedCategory);
                        }
                    }}
                />

                {/* Delete Confirmation Modal */}
                <DeleteConfirmModal
                    isOpen={deleteModal.isOpen}
                    onClose={closeDeleteModal}
                    onConfirm={handleConfirmDelete}
                    categoryName={deleteModal.category?.name}
                    hasSubcategories={deleteModal.category ? hasSubcategories(deleteModal.category.id) : false}
                    loading={deleting}
                />
            </div>
        </AdminLayout>
    );
};

export default CategoriesPage;
